import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, inject, OnDestroy, OnInit, Renderer2, signal, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { filter, forkJoin } from 'rxjs';
import * as UserActions from '@auth/store/auth.actions';
import { logout } from '@auth/store/auth.actions';
import * as DataActions from '../store/data.actions';
import { AuthService } from '../auth/services/auth.service';
import { AppSidebarComponent } from './app.sidebar.component';
import { AppTopBarComponent } from './app.top-bar.component';
import { ToastCloseEvent } from 'primeng/toast';
import { BreadcrumbService } from './services/breadcrumb.service';
import { RoleService } from '../shared/services/role.service';
import { PAGE_STATE } from '../shared/enums/page-state.enum';
import { MenuItem } from 'primeng/api';
import { AbstractLayoutPageComponent } from '../shared/abstracts/abstract-layout-page.component';
import { LayoutService } from './services/app.layout.service';
import { AuthorityDto, ClientDto, ExceptionResponse, PermissionDto, ProfileDto } from '../shared/dto';

@Component({
  selector: 'app-partials',
  templateUrl: './app.layout.page.html',
  providers: [],
  standalone: false,
})
export class AppLayoutPage extends AbstractLayoutPageComponent implements OnInit, OnDestroy {
  /**
   * The shell needs the full layout service (menu, overlay, config). Shared
   * only sees the theme port, so the concrete service is bound here.
   */
  protected override readonly layoutService = inject(LayoutService);

  private readonly roleService = inject(RoleService);

  /**
   * Same rule the role gate uses, read from the one place that defines it: the
   * sidebar and the menu toggle have to disappear on exactly the condition that
   * replaces the page content with the denial panel.
   */
  protected readonly canAccessPlatform = this.roleService.canAccessPlatform;

  private breadcrumbService = inject(BreadcrumbService);
  private renderer = inject(Renderer2);
  private authService = inject(AuthService);

  message = signal<string>('');

  menuOutsideClickListener: (() => void) | null = null;

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

  @ViewChild(AppTopBarComponent) appTopBar!: AppTopBarComponent;

  constructor() {
    super();
    this.layoutService.overlay$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          if (this.isOutsideClicked(event)) {
            this.hideMenu();
          }
        });
      }
      if (this.layoutService.state().staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef), filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();
      this.handleRouteData();
      this.pageService.reset(PAGE_STATE.ACTIONS);
    });
  }

  ngOnInit() {
    this.state.loading.set(true);
    forkJoin([
      this.authService.readProfile(),
      this.authService.readClient(),
      this.authService.readAuthorities(),
      this.authService.readPermissions(),
      // this.translationService.read()
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (responses) => {
        const client: ClientDto = responses[1];
        this.store.dispatch(UserActions.setUserClient({ client }));
        const profile: ProfileDto = responses[0];
        this.store.dispatch(UserActions.setUserProfile({ profile }));
        const authorities: AuthorityDto[] = responses[2];
        this.store.dispatch(UserActions.setUserAuthorities({ authorities }));
        const permissions: PermissionDto[] = responses[3];
        this.store.dispatch(UserActions.setUserPermissions({ permissions }));
        const translations: any[] = [];
        this.store.dispatch(DataActions.setDataTranslations({ translations }));
        const authorityNames = authorities.map(a => a.name) || [];
        if (authorityNames.length === 0) {
          this.logger.warn(this.constructor.name, 'No authorities found.');
          this.store.dispatch(logout());
          this.router.navigate(['auth', 'login']).catch(e => this.logger.error(e));
          this.state.loading.set(false);
          return;
        }
        // const isSuperAdmin = authorityNames.includes('SUPER_ADMIN');
        // const isProfessional = authorityNames.includes('PROFESSIONAL');
        // if (!isSuperAdmin && !isProfessional) {
        //   this.logger.warn(this.constructor.name, 'Work in progress!');
        //   this.router.navigate(['not-implemented']).catch(e => this.logger.error(e));
        //   this.state.loading.set(false);
        //   return;
        // }
        this.state.loading.set(false);
      },
      error: (error: ExceptionResponse) => {
        this.logger.error(this.constructor.name, 'Error:', error);
        this.layoutToastService.showError$($localize`Errore in fase di autenticazione`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.store.dispatch(logout());
            this.router.navigate(['auth', 'login']).catch(e => this.logger.error(e));
          },
        });
      },
    });
  }

  ngOnDestroy() {
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }

  isOutsideClicked(event: MouseEvent) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button');
    const eventTarget = event.target as Node;
    return !(sidebarEl?.isSameNode(eventTarget) || sidebarEl?.contains(eventTarget) || topbarEl?.isSameNode(eventTarget) || topbarEl?.contains(eventTarget));
  }

  hideMenu() {
    this.layoutService.state.update((prev: any) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  handleRouteData() {
    let route = this.route;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.logger.debug(this.name, 'Route data', `${JSON.stringify(route.snapshot.data)}`);
    // const { prefix, title, subtitle } = route.snapshot.data;
    // this.pageService.breadcrumbs.set(breadcrumbs || null);
    // this.pageService.prefix.set(prefix || null);
    // this.pageService.title.set(title || null);
    // this.pageService.subtitle.set(subtitle || null);
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }
  }

  onToastClose(event: ToastCloseEvent): void {
    this.layoutToastService.onClose.set(event);
  }

  get containerClass() {
    return {
      'layout-no-sidebar': !this.canAccessPlatform(),
      'layout-overlay': this.layoutService.config().menuMode === 'overlay',
      'layout-static': this.layoutService.config().menuMode === 'static',
      'layout-static-inactive': this.layoutService.state().staticMenuDesktopInactive && this.layoutService.config().menuMode === 'static',
      'layout-overlay-active': this.layoutService.state().overlayMenuActive,
      'layout-mobile-active': this.layoutService.state().staticMenuMobileActive,
    };
  }

  get home(): MenuItem {
    return this.breadcrumbService.home;
  }

  get breadcrumbs(): MenuItem[] {
    return this.breadcrumbService.breadcrumbs;
  }

  get hasBreadcrumbs(): boolean | null {
    return this.pageService.hasBreadcrumbs();
  }

  get tplHeader(): TemplateRef<any> | null {
    return this.pageService.tplHeader();
  }

  get tplActions(): TemplateRef<any> | null {
    return this.pageService.tplActions();
  }

  get tplKPIs(): TemplateRef<any> | null {
    return this.pageService.tplKPIs();
  }

  get empty(): boolean {
    return !this.breadcrumbs && !this.tplHeader && !this.tplActions;
  }
}
